import { editorBlockFragment } from '@getaccept/editor-lib';
import gql from 'graphql-tag';
import { AttachmentFragments } from '../../attachments/graphql/attachment.fragments';

export class DocumentFragments {
  static tagFragment = gql`
    fragment tagFragment on Tag {
      title
      status
      id
    }
  `;

  static documentUserFragment = gql`
    fragment documentUserFragment on User {
      email
      firstName
      lastName
      fullName
      id
      thumbUrl
      title
      mobile
      signature
      signatureBase30
    }
  `;

  static documentListUserFragment = gql`
    fragment documentListUserFragment on User {
      id
      fullName
      thumbUrl
      email
    }
  `;

  static documentListRecipientFragment = gql`
    fragment documentListRecipientFragment on Recipient {
      id
      companyName
      fullName
      thumbUrl
      email
    }
  `;

  static recipientFragment = gql`
    fragment recipientFragment on Recipient {
      firstName
      lastName
      fullName
      companyName
      companyNumber
      title
      mobile
      email
      thumbUrl
      gender
      role
      roleName
      id
      orderNum
      verifyQna
      verifyQnaOpen
      verifyQnaSign
      verifyQnaQuestion
      verifyQnaAnswer
      verifySms
      verifySmsOpen
      verifySmsSign
      verifyLinkedin
      verifyEid
      verifyEidType
      createdAt
      phone
      note
      status
      contactId
      signature
      settings {
        showSSNPermission
      }
    }
  `;

  static documentVideoFragment = gql`
    fragment documentVideoFragment on DocumentVideo {
      video {
        id
        thumbUrl
        videoUrl
        videoType
      }
    }
  `;

  static contentLinkBlockFragment = gql`
    fragment contentLinkBlockFragment on ContentLinkBlock {
      buttonText
      description
      id
      thumbUrl
      thumbExtension
      title
      url
    }
  `;
  static videoBlockFragment = gql`
    fragment videoBlockFragment on Video {
      id
      thumbUrl
      videoUrl
      videoType
      videoTitle
    }
  `;

  static pageFragment = gql`
    fragment pageFragment on DocumentPage {
      id
      pageType
      pageNum
      pageHeight
      pageWidth
      cropBoxH
      cropBoxW
      thumbUrl
      video {
        id
        thumbUrl
        videoUrl
        videoType
        videoTitle
      }
      contentLinkBlock {
        buttonText
        description
        id
        thumbUrl
        thumbExtension
        title
        url
      }
      editorBlock {
        ...editorBlockFragment
      }
    }
    ${editorBlockFragment}
  `;

  static manuallySignedPageFragment = gql`
    fragment manuallySignedPageFragment on SignaturePage {
      pageWidth
      pageHeight
      pageNum
      thumbUrl
    }
  `;

  static documentRevisionsFragment = gql`
    fragment documentRevisionsFragment on DocumentRevision {
      message
      revision
      status
      editorBlockContentVersions {
        blockId
        contentId
        versions {
          versionId
          lastModified
          isLatest
          isPublished
        }
      }
    }
  `;
  static documentFragment = gql`
    fragment documentFragment on Document {
      id
      name
      companyName
      status
      isSigning
      isSigningOrder
      isSelfsign
      type
      autoCommentText
      autoCommentEmail
      isAutoComment
      isAutoCommentEmail
      value
      contractStartDate
      contractEndDate
      expirationDate
      sendDate
      signDate
      createdAt
      scheduledSendingTime
      scheduledSendingDate
      isSigningBiometric
      isSigningInitials
      isSigningForward
      isPrivate
      previewUrl
      externalEditorId
      externalEditorType
      uniqueId
      hasWriteAccess
      isSmsReminder
      isReminderSending
      isIdentifyRecipient
      reminderOpened
      reminderOpenedDays
      reminderSigned
      reminderSignedDays
      reminderRepeat
      reminderRepeatDays
      reminderExpiration
      reminderExpirationDays
      isSmsSending
      isScheduledSending
      tags {
        ...tagFragment
      }
      user {
        ...documentUserFragment
      }
      recipients {
        ...recipientFragment
      }
      video {
        ...documentVideoFragment
      }
      pages {
        ...pageFragment
      }
      manuallySignedPages {
        ...manuallySignedPageFragment
      }
      documentAttachments {
        ...documentAttachmentFragment
      }
      documentRevisions {
        ...documentRevisionsFragment
      }
    }
    ${AttachmentFragments.documentAttachment}
    ${DocumentFragments.tagFragment}
    ${DocumentFragments.documentUserFragment}
    ${DocumentFragments.recipientFragment}
    ${DocumentFragments.documentVideoFragment}
    ${DocumentFragments.manuallySignedPageFragment}
    ${DocumentFragments.documentRevisionsFragment}
    ${DocumentFragments.pageFragment}
  `;

  static documentWithoutAttachmentsFragment = gql`
    fragment documentWithoutAttachmentsFragment on Document {
      id
      name
      companyName
      status
      isSigning
      isSigningOrder
      isSelfsign
      type
      autoCommentText
      autoCommentEmail
      isAutoComment
      isAutoCommentEmail
      value
      contractStartDate
      contractEndDate
      expirationDate
      sendDate
      signDate
      createdAt
      scheduledSendingTime
      scheduledSendingDate
      isSigningBiometric
      isSigningInitials
      isSigningForward
      isPrivate
      previewUrl
      externalEditorId
      externalEditorType
      uniqueId
      hasWriteAccess
      isSmsReminder
      isReminderSending
      isIdentifyRecipient
      reminderOpened
      reminderOpenedDays
      reminderSigned
      reminderSignedDays
      reminderRepeat
      reminderRepeatDays
      reminderExpiration
      reminderExpirationDays
      isSmsSending
      isScheduledSending
      tags {
        ...tagFragment
      }
      user {
        ...documentUserFragment
      }
      recipients {
        ...recipientFragment
      }
      video {
        ...documentVideoFragment
      }
      pages {
        ...pageFragment
      }
      manuallySignedPages {
        ...manuallySignedPageFragment
      }
      documentRevisions {
        ...documentRevisionsFragment
      }
    }
    ${DocumentFragments.tagFragment}
    ${DocumentFragments.documentUserFragment}
    ${DocumentFragments.recipientFragment}
    ${DocumentFragments.documentVideoFragment}
    ${DocumentFragments.manuallySignedPageFragment}
    ${DocumentFragments.documentRevisionsFragment}
    ${DocumentFragments.pageFragment}
  `;

  static documentListFragment = gql`
    fragment documentListFragment on DocumentDto {
      id
      name
      companyName
      status
      value
      expirationDate
      tags {
        ...tagFragment
      }
      user {
        ...documentListUserFragment
      }
      recipients {
        ...documentListRecipientFragment
      }
      type
      signDate
      createdAt
      isPrivate
    }
    ${DocumentFragments.tagFragment}
    ${DocumentFragments.documentListUserFragment}
    ${DocumentFragments.documentListRecipientFragment}
  `;
}
